// The current page, on mobile: a closed strip named for the page that opens a bottom sheet
// holding the page labels and the page itself. `DecisionsPanel.tsx` no longer has a desktop
// sibling for this; on desktop the page is a column beside the conversation.
//
// This has to be handed to `Conversation.tsx`/`PrivateChat.tsx` as a prop and rendered
// INSIDE their own `.conversation__foot`, directly above the composer, rather than living
// as a `.planBody` sibling: a sibling is laid out BESIDE the conversation column, and the
// composer is owned by those two components. CSS (`.decisionsSheet`, `styles.css`) hides
// this on desktop.
//
// No `position: fixed` anywhere here (forbidden in `styles.css`, see its top-of-file
// comment): the scrim and sheet are `position: absolute` against `#root`, the same
// viewport-safe technique `.accountMenu__panel` and the operator debug panel use.

import { useEffect, useState, type ReactNode } from 'react';

export type DecisionsMobileStripProps = {
  /** The current page's name. */
  label: string;
  /** What the sheet holds: the page labels and the page. */
  children: ReactNode;
};

export function DecisionsMobileStrip({
  label,
  children,
}: DecisionsMobileStripProps): JSX.Element {
  const [open, setOpen] = useState(false);

  // Escape closes the sheet, matching every other dismissable surface in this package
  // (`AccountMenu`).
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <div className="decisionsSheet">
      <button
        type="button"
        className="decisionsSheet__strip"
        onClick={() => setOpen(true)}
      >
        {label} ›
      </button>
      {open ? (
        <div className="decisionsSheet__scrim" onClick={() => setOpen(false)}>
          <div
            className="decisionsSheet__sheet"
            role="dialog"
            aria-label={label}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="decisionsSheet__handle" aria-hidden="true" />
            <div className="decisionsPanel__head">
              <button
                type="button"
                className="iconButton"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
            {children}
          </div>
        </div>
      ) : null}
    </div>
  );
}
